/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import { RichText } from '@/components/common/RichText';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles, RefreshCw } from 'lucide-react';
import {
  greetingForHour, useDailyBriefing, BriefingSkeleton, BriefingStats, buildBriefingStats,
} from '@/components/briefing/briefingParts';

/**
 * Sprint 9D — Home paziente "cinematografica".
 * Personalized AI-generated daily briefing card with time-aware greeting,
 * staggered framer-motion entry and deep-links to relevant sections.
 *
 * Refactor note (Code Quality): data hook, greeting logic, skeleton and stat
 * tiles live in `components/briefing/briefingParts.jsx`.
 */
const DailyBriefingCard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data, loading, refreshing, refresh } = useDailyBriefing();

  if (loading) return <BriefingSkeleton />;
  if (!data?.text) return null;

  const greet = greetingForHour(new Date().getHours(), t, user?.full_name);
  const GreetIcon = greet.icon;
  const stats = buildBriefingStats(data.highlights, t);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      data-testid="daily-briefing-card"
    >
      <Card className={`relative overflow-hidden p-5 rounded-3xl border-primary/25 bg-gradient-to-br ${greet.tone}`}>
        {/* Decorative aurora blob */}
        <motion.div
          aria-hidden
          className="absolute -top-14 -right-10 h-40 w-40 rounded-full bg-primary/25 blur-3xl"
          animate={{ scale: [1, 1.12, 1], opacity: [0.5, 0.75, 0.5] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />
        <div className="relative flex items-start gap-3">
          <motion.div
            initial={{ scale: 0.6, rotate: -12, opacity: 0 }}
            animate={{ scale: 1, rotate: 0, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 220, damping: 15, delay: 0.1 }}
            className="h-11 w-11 rounded-2xl bg-primary/25 text-primary flex items-center justify-center flex-shrink-0 shadow-inner"
          >
            <GreetIcon className="h-5 w-5" />
          </motion.div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <motion.div
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15 }}
                className="font-display font-semibold text-base leading-tight"
              >
                {greet.label}
              </motion.div>
              <Button
                variant="ghost" size="icon"
                className="h-7 w-7 -my-1"
                onClick={refresh}
                disabled={refreshing}
                title={t('refresh')}
                data-testid="daily-briefing-refresh"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-primary/70 mt-0.5">
              <Sparkles className="h-3 w-3" />
              <span>{t('dailyBriefingTitle')}</span>
            </div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.25, duration: 0.5 }}
              className="text-sm mt-2"
              data-testid="daily-briefing-text"
            >
              <RichText text={data.text} />
            </motion.div>
            <BriefingStats stats={stats} />
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

export default DailyBriefingCard;
